import {
  Query,
  QueryResultsAre,
  Question,
} from '@jupiterone/jupiter-types/question';
import { CloseButton, JupiterTheme } from '@jupiterone/jupiter-web-toolkit';
import {
  Button,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { Field } from 'formik';
import get from 'lodash.get';
import startCase from 'lodash/startCase';
import React from 'react';
import HelpIcon from 'react-feather/dist/icons/help-circle';
import AddIcon from 'react-feather/dist/icons/plus';
import FormTextField from './FormTextField';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    addIcon: {
      height: 24,
      strokeWidth: 1.25,
      width: 24,
    },
    addButton: {
      background: theme.palette.primary.main,
      borderRadius: 18,
      border: 'none',
      color: theme.palette.background.paper,
      height: 36,
      minWidth: 36,
      padding: 0,
      pointerEvents: 'all',
      marginLeft: 'auto',
      '&:hover': {
        background: theme.palette.primary.dark,
      },
    },
    query: {
      fontFamily: `'Ubuntu Mono', monospace`,
    },
    remove: {
      marginLeft: theme.spacing(-1),
    },
    tooltip: {
      background: theme.palette.background.paper,
      color: theme.palette.primary.main,
      padding: theme.spacing(1.5),
      fontWeight: 'normal',
    },
    iconButton: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(1),
    },
    sectionDivider: {
      backgroundColor: theme.palette.grey[500],
      height: theme.spacing(0.375),
      marginBottom: theme.spacing(2),
    },
    queryNumber: {
      marginRight: theme.spacing(4),
      marginTop: theme.spacing(2),
    },
    removeQueryButtonContainer: {
      marginLeft: theme.spacing(3),
      marginTop: theme.spacing(),
    },
    sectionHeader: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: theme.spacing(),
    },
    queryContainer: {
      display: 'flex',
      marginBottom: theme.spacing(1),
    },
    queryTextFieldsContainer: {
      flexGrow: 1,
    },
    queryNameField: {
      [theme.breakpoints.up('md')]: {
        paddingRight: theme.spacing(3),
      },
    },
  })
);

const QUERY_RESULTS_ARE: QueryResultsAre[] = [
  QueryResultsAre.INFORMATIVE,
  QueryResultsAre.GOOD,
  QueryResultsAre.BAD,
  QueryResultsAre.UNKNOWN,
];

interface Props {
  disabled?: boolean;
  onChange: any;
  question: Question;
  setFormValues: (question: Question) => void;
}

const QueryFormFields = ({
  disabled,
  onChange,
  question,
  setFormValues,
}: Props) => {
  const classes = useStyles();

  const handleAddQuery = () => {
    setFormValues({
      ...question,
      queries: [...question.queries, { name: '', query: '' }],
    });
  };

  const handleRemoveQuery = (index: number) => () => {
    question.queries.splice(index, 1);
    setFormValues({ ...question });
  };

  return (
    <>
      <div className={classes.sectionHeader}>
        <Typography color="inherit">Queries</Typography>
        <Tooltip
          title="Each question can have one or multiple queries. Query name is optional. Queries named with `good`, `bad`, `unknown` are used to perform continuous compliance gap analysis."
          placement="bottom"
          classes={{
            tooltip: classes.tooltip,
          }}
        >
          <IconButton className={classes.iconButton}>
            <HelpIcon size={20} />
          </IconButton>
        </Tooltip>
        {!disabled && (
          <Tooltip title="Add a query">
            <Button
              id="add-a-query"
              variant="outlined"
              size="small"
              className={classes.addButton}
              onClick={handleAddQuery}
            >
              <AddIcon className={classes.addIcon} />
            </Button>
          </Tooltip>
        )}
      </div>

      <Divider className={classes.sectionDivider} />

      {question.queries.map((_: Query, index: number) => {
        const queryQuery = `queries.${index}.query`;
        const queryName = `queries.${index}.name`;
        const queryResultsAre = `queries.${index}.resultsAre`;
        return (
          <div key={index} className={classes.queryContainer}>
            <Typography variant="h5" className={classes.queryNumber}>
              {index + 1}
            </Typography>
            <Grid
              container
              spacing={0}
              className={classes.queryTextFieldsContainer}
            >
              <Grid item xs={12} md={8}>
                <Field key={queryName} name={queryName}>
                  {({ field, form }: any) => (
                    <FormTextField
                      name={field.name}
                      value={field.value}
                      fullWidth
                      label="Name"
                      disabled={disabled}
                      onChange={onChange}
                      errorText={
                        get(form.touched, field.name) &&
                        get(form.errors, field.name)
                      }
                      className={classes.queryNameField}
                    />
                  )}
                </Field>
              </Grid>
              <Grid item xs={12} md={4}>
                <Field key={queryResultsAre} name={queryResultsAre}>
                  {({ field, form }: any) => (
                    <FormTextField
                      select
                      name={field.name}
                      value={field.value}
                      fullWidth
                      label="Query results are"
                      disabled={disabled}
                      onChange={onChange}
                      errorText={
                        get(form.touched, field.name) &&
                        get(form.errors, field.name)
                      }
                    >
                      {QUERY_RESULTS_ARE.map((resultsAre) => (
                        <MenuItem key={resultsAre} value={resultsAre}>
                          {startCase(resultsAre.toLowerCase())}
                        </MenuItem>
                      ))}
                    </FormTextField>
                  )}
                </Field>
              </Grid>
              <Grid item xs={12}>
                <Field key={queryQuery} name={queryQuery}>
                  {({ field, form }: any) => (
                    <FormTextField
                      classes={{ textInput: classes.query }}
                      name={field.name}
                      value={field.value}
                      label="Query"
                      disabled={disabled}
                      onChange={onChange}
                      fullWidth
                      errorText={
                        get(form.touched, field.name) &&
                        get(form.errors, field.name)
                      }
                    />
                  )}
                </Field>
              </Grid>
            </Grid>
            {index > 0 && !disabled && (
              <div className={classes.removeQueryButtonContainer}>
                <CloseButton
                  className={classes.remove}
                  label="Remove query"
                  size={20}
                  onClose={handleRemoveQuery(index)}
                />
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};

export default QueryFormFields;
